import { useCallback, useMemo } from 'react';
import { getMenus } from '../utils/auth.util';

const useMenu = ({ statisMenus }) => {
  const fetchedMenus = useMemo(() => getMenus() || [], []);

  // Default menus that should always be included
  const defaultMenuIds = ['dashboard'];

  // Normalize strings for comparison
  const normalizeString = useCallback(str => {
    if (!str) return '';
    return str
      .toLowerCase()
      .replace(/[\s_-]+/g, '')
      .replace(/[^a-z0-9]/g, '');
  }, []);

  // Deep compare menu items including subItems
  const compareMenuItems = useCallback(
    (staticMenu, fetchedMenu) => {
      const staticLabel = normalizeString(staticMenu.label);
      const fetchedLabel = normalizeString(fetchedMenu.label);
      const fetchedMenuLabel = normalizeString(fetchedMenu.menuLabel);
      return staticLabel === fetchedLabel || staticLabel === fetchedMenuLabel;
    },
    [normalizeString],
  );

  // Get display order from fetched menu
  const getDisplayOrder = staticMenu => {
    if (staticMenu.keepStaticOrder) {
      return Number.MAX_SAFE_INTEGER; // push it to sorting after preserving
    }
    // Special case for dashboard - always show first
    if (staticMenu.id === 'dashboard') {
      return -1;
    }

    const matchedMenu = fetchedMenus.find(menu =>
      compareMenuItems(staticMenu, menu),
    );
    return matchedMenu?.displayOrder ?? 999; // Default to high number if no order found
  };

  // Filter matching menus and include default menus
  const filterMatchingMenus = () => {
    if (!Array.isArray(statisMenus) || !Array.isArray(fetchedMenus)) {
      return [];
    }

    const bypassDefaultMenus = ['enquiry', 'admin'];

    const filterSubItems = subItems => {
      if (!Array.isArray(subItems)) return null;
      return subItems.filter(
        subItem =>
          defaultMenuIds.includes(subItem.id) ||
          fetchedMenus.some(menu => compareMenuItems(subItem, menu)),
      );
    };

    const matchingMenus = statisMenus.filter(staticMenu => {
      const menuMatches =
        defaultMenuIds.includes(staticMenu.id) ||
        fetchedMenus.some(menu => compareMenuItems(staticMenu, menu));

      if (menuMatches) {
        // If menu matches, filter its subItems
        if (!bypassDefaultMenus?.includes(staticMenu?.id)) {
          staticMenu.subItems = filterSubItems(staticMenu.subItems);
        }
        return true;
      }

      // If menu doesn't match directly, check if any subItems match
      const matchingSubItems = filterSubItems(staticMenu.subItems);
      if (matchingSubItems?.length > 0) {
        staticMenu.subItems = matchingSubItems;
        return true;
      }
      // If no match found, return false
      return false;
    });

    let sortedMenus = matchingMenus.sort((a, b) => {
      const orderA = getDisplayOrder(a);
      const orderB = getDisplayOrder(b);
      return orderA - orderB;
    });

    const anchorId = 'holiday'; // after this
    const stopBeforeId = 'enquiry'; // and before this (if present)
    const moveIds = ['bonus', 'performance', 'tickets'];

    const moving = moveIds
      .map(id => sortedMenus.find(m => m.id === id))
      .filter(Boolean);

    if (moving.length) {
      const movingSet = new Set(moveIds);
      sortedMenus = sortedMenus.filter(m => !movingSet.has(m.id));

      const anchorIdx = sortedMenus.findIndex(m => m.id === anchorId);

      if (anchorIdx !== -1) {
        sortedMenus.splice(anchorIdx + 1, 0, ...moving);
      } else {
        const enquiryIdx = sortedMenus.findIndex(m => m.id === stopBeforeId);
        if (enquiryIdx !== -1) {
          sortedMenus.splice(enquiryIdx, 0, ...moving);
        } else {
          sortedMenus.push(...moving);
        }
      }
    }

    return sortedMenus;
  };

  return filterMatchingMenus();
};

export default useMenu;
